import { useEffect, useRef, useState } from "react";

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const paymentMethods = [
  {
    id: "paypal",
    icon: "ri-paypal-line",
    name: "PayPal",
    color: "text-[#003087]",
    bg: "bg-[#003087]/8",
    border: "border-[#003087]/20",
    desc: "Pago seguro con tu cuenta PayPal o tarjeta de crédito/débito.",
    tags: ["Inmediato", "Internacional", "Seguro"],
    link: "https://paypal.me/ashiaka303",
  },
  {
    id: "stripe",
    icon: "ri-bank-card-line",
    name: "Stripe",
    color: "text-[#635BFF]",
    bg: "bg-[#635BFF]/8",
    border: "border-[#635BFF]/20",
    desc: "Pago con tarjeta de crédito o débito Visa, Mastercard y American Express.",
    tags: ["Visa", "Mastercard", "AMEX"],
  },
  {
    id: "mercadopago",
    icon: "ri-secure-payment-line",
    name: "Mercado Pago",
    color: "text-[#009EE3]",
    bg: "bg-[#009EE3]/8",
    border: "border-[#009EE3]/20",
    desc: "Paga con Mercado Pago y accede a meses sin intereses con tarjetas participantes.",
    tags: ["3 MSI", "6 MSI", "12 MSI"],
    highlight: true,
  },
  {
    id: "transferencia",
    icon: "ri-bank-line",
    name: "Transferencia Bancaria",
    color: "text-[#0A1F44]",
    bg: "bg-[#0A1F44]/8",
    border: "border-[#0A1F44]/20",
    desc: "Realiza tu pago por SPEI o transferencia bancaria directa. Te enviamos los datos por WhatsApp.",
    tags: ["SPEI", "Sin comisión", "Inmediato"],
  },
];

const specialPlans = [
  {
    icon: "ri-calendar-2-line",
    title: "Pago Quincenal",
    desc: "Fracciona tu curso en pagos quincenales cómodos. Ideal para quienes prefieren distribuir su inversión.",
    color: "text-[#D4AF37]",
    bg: "from-[#D4AF37]/10 to-[#D4AF37]/5",
    border: "border-[#D4AF37]/25",
    badge: "Flexible",
  },
  {
    icon: "ri-shield-check-line",
    title: "Pago Único con Descuento",
    desc: "Liquida tu programa en un solo pago y obtén un descuento especial. Consulta el porcentaje con tu asesor.",
    color: "text-emerald-600",
    bg: "from-emerald-500/10 to-emerald-500/5",
    border: "border-emerald-500/25",
    badge: "Ahorra más",
  },
];

export default function PaymentMethods() {
  const { ref, inView } = useInView();

  return (
    <section id="pagos" className="py-20 md:py-28 bg-[#F8F6F0]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase">Facilidades de Pago</span>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0A1F44] mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Invierte en tu transformación
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto">
            Ofrecemos múltiples formas de pago para que nada te detenga en tu camino hacia el conocimiento y la sanación.
          </p>
        </div>

        {/* Payment Methods Grid */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14 transition-all duration-700 delay-100 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {paymentMethods.map((method) => {
            const inner = (
              <>
                {method.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0A1F44] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                    ★ Recomendado
                  </span>
                )}
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${method.bg} mb-4`}>
                  <i className={`${method.icon} text-2xl ${method.color}`}></i>
                </div>
                <h3 className="text-[#0A1F44] font-bold text-base mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {method.name}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{method.desc}</p>
                <div className="flex flex-wrap gap-1.5">
                  {method.tags.map((tag) => (
                    <span key={tag} className={`text-xs font-medium px-2.5 py-1 rounded-full ${method.bg} ${method.color} border ${method.border}`}>
                      {tag}
                    </span>
                  ))}
                </div>
                {method.link && (
                  <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-[#003087]">
                    <i className="ri-external-link-line text-sm"></i>
                    <span>Pagar con PayPal</span>
                  </div>
                )}
              </>
            );

            return method.link ? (
              <a
                key={method.id}
                href={method.link}
                target="_blank"
                rel="noopener noreferrer"
                className={`relative bg-white rounded-2xl p-6 border ${method.border} hover:border-[#D4AF37]/50 hover:shadow-sm transition-all duration-300 group cursor-pointer block`}
              >
                {inner}
              </a>
            ) : (
              <div
                key={method.id}
                className={`relative bg-white rounded-2xl p-6 border ${method.border} hover:border-[#D4AF37]/50 transition-all duration-300 group`}
              >
                {inner}
              </div>
            );
          })}
        </div>

        {/* Special Plans */}
        <div className={`transition-all duration-700 delay-200 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-8">
            <h3 className="text-xl md:text-2xl font-bold text-[#0A1F44]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Planes y Facilidades Especiales
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {specialPlans.map((plan) => (
              <div
                key={plan.title}
                className={`relative bg-gradient-to-br ${plan.bg} rounded-2xl p-6 border ${plan.border} transition-all duration-300 hover:scale-[1.02]`}
              >
                <span className={`absolute top-4 right-4 text-xs font-bold px-2.5 py-1 rounded-full bg-white ${plan.color} border ${plan.border}`}>
                  {plan.badge}
                </span>
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl bg-white mb-4`}>
                  <i className={`${plan.icon} text-xl ${plan.color}`}></i>
                </div>
                <h4 className="text-[#0A1F44] font-bold text-base mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {plan.title}
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed">{plan.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Security badges */}
        <div className={`mt-8 flex flex-wrap items-center justify-center gap-6 transition-all duration-700 delay-400 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {[
            { icon: "ri-shield-keyhole-line", text: "Pagos 100% seguros" },
            { icon: "ri-lock-line", text: "Datos encriptados SSL" },
            { icon: "ri-customer-service-2-line", text: "Soporte personalizado" },
            { icon: "ri-refund-2-line", text: "Garantía de satisfacción" },
          ].map((badge) => (
            <div key={badge.text} className="flex items-center gap-2 text-gray-500 text-sm">
              <i className={`${badge.icon} text-[#D4AF37] text-base`}></i>
              <span>{badge.text}</span>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
